"use client";

import React, { useState } from "react";
import { Code2, Eye, Sparkles, Check, Copy } from "lucide-react";
import { CodeHighlight } from "@/components/CodeHighlight";

interface MdxPreset {
  id: string;
  name: string;
  badge: string;
  mdxInput: string;
  previewTitle: string;
  renderedType: "callout" | "grid" | "interactive";
}

const PRESETS: MdxPreset[] = [
  {
    id: "callout",
    name: "Semantisches Callout",
    badge: "Information",
    previewTitle: "Callout-Rendering",
    renderedType: "callout",
    mdxInput: `## Architektur-Tipp\n\nNutze MDX für technische Dokumentation:\n\n<TileCallout type="tip" title="Zero Runtime">\n  Statische Markdown-Texte rendern als reines Server-HTML.\n  Nur interaktive Inseln laden Client-JavaScript.\n</TileCallout>`,
  },
  {
    id: "grid",
    name: "Interaktives Raster",
    badge: "2-Spalten Grid",
    previewTitle: "Grid-Rendering",
    renderedType: "grid",
    mdxInput: `<TileGrid cols={2}>\n  <TileCard title="Server-First" badge="RSC">\n    Schneller First Paint ohne Hydration-Overhead.\n  </TileCard>\n  <TileCard title="Client-Islands" badge="use client">\n    Isolierte Interaktivität punktgenau dosiert.\n  </TileCard>\n</TileGrid>`,
  },
  {
    id: "interactive",
    name: "Live Client-Widget",
    badge: "State & Hooks",
    previewTitle: "Client-Island",
    renderedType: "interactive",
    mdxInput: `export function LiveCounter() {\n  const [count, setCount] = useState(0);\n  return (\n    <button onClick={() => setCount(c => c + 1)}>\n      Klicks: {count}\n    </button>\n  );\n}`,
  },
];

export default function MdxPlayground() {
  const [selectedId, setSelectedId] = useState<string>("callout");
  const [counter, setCounter] = useState(0);
  const [copied, setCopied] = useState(false);

  const current = PRESETS.find((p) => p.id === selectedId) || PRESETS[0];

  const handleCopy = () => {
    navigator.clipboard.writeText(current.mdxInput);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="flex flex-col gap-5 p-4 sm:p-6 min-h-[360px]">
      {/* Preset Switcher */}
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-2 block">
          Wähle ein MDX-Muster:
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {PRESETS.map((p) => {
            const isActive = p.id === selectedId;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setSelectedId(p.id)}
                className={`flex flex-col items-start p-3 rounded-xl border text-left transition-all min-h-[48px] ${
                  isActive
                    ? "border-purple-500 bg-purple-500/10 text-purple-900 dark:text-purple-200 dark:border-purple-500/50 shadow-xs"
                    : "border-neutral-200/80 bg-neutral-50 hover:bg-neutral-100 text-neutral-700 dark:border-neutral-800 dark:bg-neutral-900/60 dark:hover:bg-neutral-800/80 dark:text-neutral-300"
                }`}
              >
                <span className="font-heading font-bold text-xs sm:text-sm">
                  {p.name}
                </span>
                <span className="text-[10px] text-neutral-500 dark:text-neutral-400 font-mono mt-0.5">
                  {p.badge}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Editor & Preview Split View */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Editor Box */}
        <div className="flex flex-col rounded-xl border border-neutral-300 dark:border-neutral-800 bg-neutral-950 overflow-hidden text-neutral-200 shadow-sm">
          <div className="flex items-center justify-between border-b border-neutral-800 bg-neutral-900/90 px-3.5 py-2 text-xs">
            <div className="flex items-center gap-1.5 font-mono text-neutral-400">
              <Code2 className="h-3.5 w-3.5 text-purple-400" />
              <span>MDX-Quellcode</span>
            </div>
            <button
              type="button"
              onClick={handleCopy}
              className="flex items-center gap-1 px-2 py-0.5 rounded bg-neutral-800 hover:bg-neutral-700 text-[11px] text-neutral-300 transition-colors"
            >
              {copied ? (
                <>
                  <Check className="h-3 w-3 text-emerald-400" />
                  <span className="text-emerald-400">Kopiert</span>
                </>
              ) : (
                <>
                  <Copy className="h-3 w-3" />
                  <span>Kopieren</span>
                </>
              )}
            </button>
          </div>
          <CodeHighlight
            code={current.mdxInput}
            language="markdown"
            className="p-4 text-xs font-mono leading-relaxed"
          />
        </div>

        {/* Live Rendered View */}
        <div className="flex flex-col rounded-xl border border-neutral-200/90 dark:border-neutral-800 bg-white dark:bg-neutral-900 overflow-hidden shadow-sm">
          <div className="flex items-center justify-between border-b border-neutral-200/80 bg-neutral-50 px-3.5 py-2 text-xs dark:border-neutral-800 dark:bg-neutral-900/90">
            <div className="flex items-center gap-1.5 font-sans font-medium text-neutral-700 dark:text-neutral-300">
              <Eye className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
              <span>Kompilierte Live-Vorschau</span>
            </div>
            <span className="text-[10px] font-mono text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20">
              RSC Output
            </span>
          </div>

          <div className="p-4 flex flex-col justify-center flex-1">
            {current.renderedType === "callout" && (
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-emerald-900 dark:text-emerald-200">
                <div className="text-xs font-bold uppercase tracking-wider mb-1">
                  💡 Zero Runtime Tipp
                </div>
                <p className="text-xs leading-relaxed">
                  Statische Markdown-Texte rendern als reines Server-HTML. Nur interaktive Inseln laden Client-JavaScript.
                </p>
              </div>
            )}

            {current.renderedType === "grid" && (
              <div className="grid grid-cols-2 gap-2">
                <div className="p-3 rounded-lg border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-800/50">
                  <div className="text-[11px] font-bold text-neutral-900 dark:text-white">Server-First</div>
                  <div className="text-[10px] text-neutral-500 mt-1">Schneller First Paint ohne Hydration-Overhead.</div>
                </div>
                <div className="p-3 rounded-lg border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-800/50">
                  <div className="text-[11px] font-bold text-neutral-900 dark:text-white">Client-Islands</div>
                  <div className="text-[10px] text-neutral-500 mt-1">Isolierte Interaktivität punktgenau dosiert.</div>
                </div>
              </div>
            )}

            {current.renderedType === "interactive" && (
              <div className="flex flex-col items-center justify-center gap-3 p-4 rounded-xl border border-dashed border-purple-500/30 bg-purple-500/5">
                <span className="text-xs text-neutral-600 dark:text-neutral-400">
                  Echte Client-Komponente im Markdown:
                </span>
                <button
                  type="button"
                  onClick={() => setCounter((c) => c + 1)}
                  className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-medium text-xs shadow-xs transition-transform active:scale-95 min-h-[38px]"
                >
                  Interaktiver Klick-Zähler: {counter}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
